import { Box, Flex, Group, Stack, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import Image from 'next/image'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import CharacterCarousel from '../../components/character_carousel'
import CrewCarousel from '../../components/crew_carousel'
import ImageFallback from '../../components/image_fallback'
import CountryInput from '../../components/inputs/country_input'
import Layout from '../../components/layout'
import MoreDetails from '../../components/more_details'
import { hitsCountries } from '../../components/platform_details'
import SeasonsCarousel from '../../components/seasons_carousel'
import SimilarCarousel from '../../components/similar_carousel'
import SourcesTabs from '../../components/sources_tabs'
import StreamingAvailability from '../../components/streaming_availability_tabs'
import TrailerButton from '../../components/trailer_button'
import { searchAPI_v2 } from '../../utils/axios'
import { getCountryNameFromISO } from '../../utils/fns'
import { languages } from '../../utils/languages'
import prerenderImportantLocales from '../../utils/prerender_locales.js'

const isHighQualityBackdrop = (imgUrl) => {
  return new Promise((resolve) => {
    const img = new window.Image()
    img.src = imgUrl
    img.onload = () => {
      const isHighQuality = img.width >= 1280
      resolve(isHighQuality)
    }
    img.onerror = () => resolve(false)
  })
}

export default function Series({ series, availabilityData, hitsData }) {
  const { locale } = useRouter()

  const t = useTranslations('Series')
  const t_genres = useTranslations('Genres')
  const t_country = useTranslations('PrimaryCountry')

  const poster = series.translation?.poster || series.poster
  const logo = series.translation?.logo || series.logo
  const title = series.translation?.title || series.title
  const synopsis = series.translation?.synopsis || series.synopsis

  const [isLogoBroken, setIsLogoBroken] = useState(false)

  const [blurBackdrop, setBlurBackdrop] = useState(true)

  const [selectedCountry, setSelectedCountry] = useState(
    locale.split('-')[1]?.toLowerCase() || 'us'
  )
  const currentAvailability = availabilityData[selectedCountry] || {
    availability: []
  }

  // Obtener los hits más recientes
  const latestHits = React.useMemo(() => {
    if (
      !hitsData?.hits ||
      !Array.isArray(hitsData.hits) ||
      hitsData.hits.length === 0
    )
      return null

    // Ordenar por fecha de inicio, la más reciente primero
    const sortedData = [...hitsData.hits].sort(
      (a, b) => new Date(b.start_date) - new Date(a.start_date)
    )

    // Retornar el valor de hits del período más reciente
    return {
      position: sortedData[0]?.position || null,
      hits: sortedData[0]?.hits || null
    }
  }, [hitsData])

  useEffect(() => {
    if (series.backdrop) {
      isHighQualityBackdrop(series.backdrop).then((isHighQuality) => {
        setBlurBackdrop(!isHighQuality)
      })
    }
  }, [series.backdrop])

  // Log hits data for debugging
  useEffect(() => {
    if (hitsData) {
      console.log('Hits data completa:', hitsData)
      if (hitsData.hits) {
        const sortedData = [...hitsData.hits].sort(
          (a, b) => new Date(b.start_date) - new Date(a.start_date)
        )
        console.log('Datos ordenados por fecha:', sortedData)
        console.log('Hits más recientes:', sortedData[0]?.hits)
      }
    }
  }, [hitsData])

  const renderMetadata = () => {
    const metadata = [
      series.primary_genre ? t_genres(series.primary_genre) : null,
      series.year,
      series.age_ratings?.US,
      t_country(getCountryNameFromISO(series.primary_country)),
      languages[series.original_language],
      series.runtime ? `${series.runtime} minutes` : null
    ]
      .filter(Boolean)
      .join(' | ')

    return (
      <Text tt="uppercase" size={12} weight={700}>
        {metadata}
      </Text>
    )
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TVSeries',
    'name': series.title,
    'image': series.poster || '/placeholder.jpg',
    'url': `https://www.streamdiscover.com/series/${series.uid || series.slug}`,
    'identifier': series.uid || series.slug,
    'inLanguage': locale || 'en',
    'description': series.synopsis || 'No synopsis available.',
    'datePublished': series.year ? series.year.toString() : 'N/A',
    'genre': series.primary_genre || 'Unknown',
    'director':
      series.directors?.map((director) => ({
        '@type': 'Person',
        'name': director
      })) || [],
    'actor':
      series.credits.cast?.map((actor) => ({
        '@type': 'Person',
        'name': actor.name
      })) || [],
    'creator':
      series.created_by?.map((creator) => ({
        '@type': 'Person',
        'name': creator
      })) || [],
    'productionCompany': series.primary_company
      ? {
          '@type': 'Organization',
          'name': series.primary_company
        }
      : undefined,
    'contentRating': series.age_ratings?.US || 'Not Rated',
    'aggregateRating': series.ratingValue
      ? {
          '@type': 'AggregateRating',
          'ratingValue': series.ratingValue,
          'bestRating': '10',
          'worstRating': '0',
          'reviewCount': series.reviewCount || '0'
        }
      : undefined,
    'numberOfSeasons': series.seasons?.length || 0,
    'trailer': series.video
      ? {
          '@type': 'VideoObject',
          'name': `${series.title} Trailer`,
          'embedUrl': `https://www.youtube.com/embed/${series.video.split('=')[1]}`,
          'thumbnailUrl': series.poster || '/placeholder.jpg',
          'uploadDate': series.year ? `${series.year}-01-01` : undefined,
          'description': `Watch the trailer for ${series.title}`
        }
      : undefined
  }

  return (
    <>
      <Head>
        <title>{`${t('titleTab')} ${title} - Stream Discover`}</title>
        <meta name="description" content={t('metaDescription', { title: title })} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
        <link href={`https://www.streamdiscover.com/series/${series.slug}`} />

        <meta property="og:title" content={`${title} - Stream Discover`} />
        <meta
          property="og:description"
          content={t('metaDescription', { title: title })}
        />
        <meta property="og:type" content="video.tv_show" />
        <meta property="og:image" content={series.poster || '/placeholder.jpg'} />
        <meta
          property="og:url"
          content={`https://www.streamdiscover.com/${locale}/series/${
            series.uid || series.slug
          }`}
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title} - Stream Discover`} />
        <meta
          name="twitter:description"
          content={t('metaDescription', { title: title })}
        />
        <meta name="twitter:image" content={series.poster || '/placeholder.jpg'} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      <div>
        <Box
          sx={{
            position: 'relative',
            width: '100vw',
            height: blurBackdrop ? '300px' : '380px'
          }}
        >
          <Image
            src={series.backdrop || '/placeholder.jpg'}
            fill
            alt={title || 'Backdrop image'}
            style={{
              objectFit: 'cover',
              filter: blurBackdrop ? 'blur(80px)' : 'none',
              marginTop: blurBackdrop ? '110px' : '10px'
            }}
            priority
          />
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background:
                'linear-gradient(to bottom, rgba(8, 17, 24, 1) 5%, rgba(8, 17, 24, 0.7) 20%, rgba(8, 17, 24, 0.6) 40%, rgba(8, 17, 24, 0.6) 60%, rgba(8, 17, 24, 0.7) 80%, rgba(8, 17, 24, 1) 100%)',
              filter: 'blur(200px)'
            }}
          />
          {!blurBackdrop && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  zIndex: 2
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(to bottom, rgba(8, 17, 24, 1) 0%, rgba(8, 17, 24, 0.7) 10%, rgba(8, 17, 24, 0.6) 30%, rgba(8, 17, 24, 0.6) 50%, rgba(8, 17, 24, 0.7) 70%, rgba(8, 17, 24, 1) 101%)',
                  zIndex: 2
                }}
              />
            </>
          )}

          <Flex
            direction={{ base: 'column', md: 'row' }}
            sx={(theme) => ({
              width: '90vw',
              maxWidth: '1366px',
              margin: '0 auto',
              alignItems: 'center',
              textAlign: 'center',
              [theme.fn.largerThan('md')]: {
                width: '80vw',
                alignItems: 'flex-start',
                textAlign: 'left'
              }
            })}
            pos="relative"
            style={{ zIndex: 3 }}
            pt={{ base: 16, md: 64 }}
            gap={24}
          >
            <Box
              display={{ base: 'none', md: 'block' }}
              sx={{
                width: '200px',
                height: '300px',
                minWidth: '200px',
                maxWidth: '200px',
                borderRadius: '4px',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <ImageFallback
                src={poster}
                fallbackSrc="/noimage.png"
                alt={title || 'Series poster'}
                priority
                style={
                  {
                    // Estilos aplicados internamente por ImageFallback
                    // objectFit: 'cover' para la imagen principal
                    // objectFit: 'contain' para el fallback
                  }
                }
              />
            </Box>

            <Stack
              spacing={16}
              w={{ base: '100%', md: '80%' }}
              align={{ base: 'center', md: 'flex-start' }}
              sx={{ color: 'white', height: 'auto' }}
            >
              <Box display={{ base: 'none', md: 'block' }}>
                {!isLogoBroken && logo ? (
                  <Image
                    src={logo}
                    alt={title || 'Series logo'}
                    width={200} // Reduced from 300
                    height={100} // Reduced from 150
                    priority
                    style={{
                      maxWidth: '40%', // Reduced from 60%
                      maxHeight: '100px', // Reduced from 150px
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain'
                    }}
                    onError={() => setIsLogoBroken(true)}
                  />
                ) : (
                  <Text size={32} sx={{ maxWidth: '100%' }}>
                    {' '}
                    {/* Reduced from 40 */}
                    {title}
                  </Text>
                )}
              </Box>

              <Box
                display={{ base: 'flex', md: 'none' }}
                sx={{
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <Box
                  sx={{
                    width: '200px',
                    height: '300px',
                    borderRadius: '4px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <ImageFallback
                    src={poster}
                    fallbackSrc="/noimage.png"
                    alt={title || 'Series poster'}
                    priority
                    style={
                      {
                        // Estilos aplicados internamente por ImageFallback
                        // objectFit: 'cover' para la imagen principal
                        // objectFit: 'contain' para el fallback
                      }
                    }
                  />
                </Box>
                <Stack spacing={8} align="center">
                  <Text size={24} weight={700}>
                    {title}
                  </Text>
                  <Text tt="uppercase" size={12} weight={700}>
                    {[
                      series.primary_genre ? t_genres(series.primary_genre) : null,
                      series.year,
                      series.age_ratings?.US !== 'Approved'
                        ? series.age_ratings?.US
                        : null,
                      t_country(getCountryNameFromISO(series.primary_country)),
                      languages[series.original_language],
                      series.runtime ? `${series.runtime} minutes` : null
                    ]
                      .filter(Boolean)
                      .join(' | ')}
                  </Text>
                </Stack>
              </Box>

              {renderMetadata()}

              <Text
                size="sm"
                sx={(theme) => ({
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'center',
                  textShadow: '0px 2px 2px rgba(123, 57, 182, 0.8)',
                  [theme.fn.largerThan('md')]: {
                    textAlign: 'left'
                  }
                })}
              >
                {synopsis || 'Sinopsis no disponible.'}
              </Text>

              <Flex gap="xs" wrap="wrap">
                {series.created_by?.length > 0 && (
                  <Group
                    spacing={4}
                    sx={{
                      textShadow: '0px 2px 2px rgba(123, 57, 182, 0.8)'
                    }}
                  >
                    <Text size="sm" fw="bold">
                      {t('creators')}:
                    </Text>
                    <Text size="sm">{series.created_by.join(', ')}</Text>
                  </Group>
                )}
                {series.primary_company && (
                  <Group
                    spacing={4}
                    sx={{
                      textShadow: '0px 2px 2px rgba(123, 57, 182, 0.8)'
                    }}
                  >
                    <Text size="sm" fw="bold">
                      {t('company')}:
                    </Text>
                    <Text size="sm">{series.primary_company}</Text>
                  </Group>
                )}
                {series.networks?.length > 0 && (
                  <Group
                    spacing={4}
                    sx={{
                      textShadow: '0px 2px 2px rgba(123, 57, 182, 0.8)'
                    }}
                  >
                    <Text size="sm" fw="bold">
                      {t('network')}:
                    </Text>
                    <Text size="sm">{series.networks[0]}</Text>
                  </Group>
                )}
              </Flex>

              {series.score > 0 && (
                <Flex align="center" gap="md">
                  <Text
                    size={10}
                    weight={700}
                    sx={{
                      backgroundColor: 'rgba(123,57,182,0.2)',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      color: 'rgba(123,57,182,1)',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Image
                      src="/scorestar.png"
                      width={10}
                      height={10}
                      alt="Score star"
                    />
                    {Number(series.score).toFixed(1)}/10
                  </Text>
                  {latestHits && (
                    <>
                      <Text
                        size={10}
                        weight={700}
                        sx={{
                          backgroundColor: 'rgba(123,57,182,0.2)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          color: 'rgba(123,57,182,1)',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        {t('hitsGlobalPosition')}: {Math.floor(latestHits.position)}
                      </Text>
                      {latestHits.hits && (
                        <Text
                          size={10}
                          weight={700}
                          sx={{
                            backgroundColor: 'rgba(123,57,182,0.2)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            color: 'rgba(123,57,182,1)',
                            letterSpacing: '0.5px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {t('hitsScore')}: {Math.floor(latestHits.hits)}/100
                        </Text>
                      )}
                    </>
                  )}
                </Flex>
              )}

              {/* Quick Insights 
              <Text
                size="md"
                weight="bold"
                mt="xs"
                sx={{
                  textShadow: '0px 2px 2px rgba(123, 57, 182, 0.8)'
                }}
              >
                QUICK INSIGHTS
              </Text>
              <Flex
                gap="lg"
                justify="flex-start"
                wrap="wrap"
                sx={{
                  position: 'relative',
                  marginBottom: '10px'
                }}
              >
                <Box
                  sx={{
                    display: 'inline-block',
                    textAlign: 'left'
                  }}
                >
                  <Flex justify="space-between" align="center">
                    <Text
                      size="md"
                      weight="bold"
                      sx={{
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {series.ratingValue ? `${series.ratingValue}/10` : 'N/A'}
                    </Text>
                    <Image
                      src="/circle.png"
                      alt="Hits Score"
                      width={34}
                      height={26}
                    />
                  </Flex>
                  <Text size="sm" color="dimmed">
                    HITS SCORE THIS WEEK
                  </Text>
                </Box>

                <Box
                  sx={{
                    display: 'inline-block',
                    textAlign: 'left'
                  }}
                >
                  <Flex justify="space-between" align="center">
                    <Text
                      size="md"
                      weight="bold"
                      sx={{
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {series.rank || '#N/A'}
                    </Text>
                    <Image src="/line.png" alt="Top Ranked" width={50} height={14} />
                  </Flex>
                  <Text size="sm" color="dimmed">
                    TOP RANKED THIS WEEK
                  </Text>
                </Box>

                <Box
                  sx={{
                    display: 'inline-block',
                    textAlign: 'left'
                  }}
                >
                  <Flex justify="space-between" align="center">
                    <Text
                      size="md"
                      weight="bold"
                      sx={{
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {series.streamDiscoverScore || '60/100'}
                    </Text>
                    <Image
                      src="/circle.png"
                      alt="Stream Discover Score"
                      width={34}
                      height={26}
                    />
                  </Flex>
                  <Text size="sm" color="dimmed">
                    STREAM DISCOVER SCORE
                  </Text>
                </Box>

                <Box
                  sx={{
                    display: 'inline-block',
                    textAlign: 'left'
                  }}
                >
                  <Flex justify="space-between" align="center">
                    <Text
                      size="md"
                      weight="bold"
                      sx={{
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {series.searchThisWeek || '2M'}
                    </Text>
                    <Image
                      src="/arrow.png"
                      alt="Search This Week"
                      width={24}
                      height={14}
                    />
                  </Flex>
                  <Text size="sm" color="dimmed">
                    SEARCH THIS WEEK
                  </Text>
                </Box>

                <Box
                  sx={{
                    width: '150px',
                    textAlign: 'left'
                  }}
                >
                  <Flex justify="space-between" align="center">
                    <Text size="md" weight="bold">
                      {series.awards || 30}
                    </Text>
                  </Flex>
                  <Text size="sm" color="dimmed">
                    AWARDS
                  </Text>
                </Box>

                <Flex
                  gap="sm"
                  direction="row"
                  align="center"
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }}
                >
                  <Flex direction="column" align="center">
                    <Text size="xs" color="dimmed">
                      5K
                    </Text>
                    <Image
                      src="/dislike.png"
                      alt="Dislikes"
                      width={21}
                      height={25}
                    />
                  </Flex>

                  <Flex direction="column" align="center">
                    <Text size="xs" color="dimmed">
                      500K
                    </Text>
                    <Image src="/like.png" alt="Likes" width={21} height={25} />
                  </Flex>
                </Flex>
              </Flex>
              */}
            </Stack>
          </Flex>
        </Box>
        <Box
          sx={(theme) => ({
            width: '90vw',
            maxWidth: '1366px',
            margin: '0 auto',
            marginTop: '24px',
            position: 'relative',
            zIndex: 3,
            [theme.fn.largerThan('md')]: {
              width: '80vw',
              marginTop: blurBackdrop ? '16px' : 0
            }
          })}
        >
          <Box
            mt={blurBackdrop ? 60 : 0}
            display={{ base: 'none', md: 'block' }}
            sx={{ width: '200px' }}
          >
            <TrailerButton
              blur={blurBackdrop}
              videoKey={series.video?.split('=')[1]}
            />
          </Box>
          <Stack mt={{ base: 300, md: 10 }}>
            <CountryInput
              sx={(theme) => ({
                width: '100%',
                height: '46px',
                gap: '10px',
                marginLeft: 0,
                fontSize: '10px',
                [theme.fn.largerThan('md')]: {
                  width: '158px',
                  marginLeft: 'auto'
                }
              })}
              value={selectedCountry}
              onChange={setSelectedCountry}
            />
            <StreamingAvailability
              id={series.uid}
              type="series"
              title={series.title}
              availabilityData={currentAvailability}
              selectedCountry={selectedCountry}
            />
            <SourcesTabs id={series.uid} type="series" />
            <CharacterCarousel
              cast={series.credits.cast}
              isLoading={false}
              title={title}
            />
            <CrewCarousel
              crew={series.credits.crew}
              isLoading={false}
              title={title}
            />
            <SeasonsCarousel
              title={title}
              seasons={series.seasons}
              isLoading={false}
            />
            <MoreDetails movie={series} title={title} />
            <SimilarCarousel
              slug={series.slug}
              uid={series.uid}
              type="series"
              vectorizer="gpt4all"
              title={title}
            />
          </Stack>
        </Box>
      </div>
    </>
  )
}

Series.getLayout = function getLayout(page) {
  return (
    <Layout withAutocomplete pageName="Serie">
      {page}
    </Layout>
  )
}

Series.propTypes = {
  series: PropTypes.shape({
    title: PropTypes.string.isRequired,
    backdrop: PropTypes.string,
    poster: PropTypes.string,
    logo: PropTypes.string,
    primary_genre: PropTypes.string,
    year: PropTypes.number,
    age_ratings: PropTypes.object,
    primary_country: PropTypes.string,
    original_language: PropTypes.string,
    runtime: PropTypes.number,
    synopsis: PropTypes.string,
    created_by: PropTypes.arrayOf(PropTypes.string),
    primary_company: PropTypes.string,
    networks: PropTypes.arrayOf(PropTypes.string),
    video: PropTypes.string,
    credits: PropTypes.shape({
      cast: PropTypes.array.isRequired,
      crew: PropTypes.array.isRequired
    }).isRequired,
    seasons: PropTypes.array.isRequired,
    slug: PropTypes.string.isRequired,
    uid: PropTypes.string.isRequired
  }).isRequired,
  availabilityData: PropTypes.shape({
    availability: PropTypes.array.isRequired
  }).isRequired,
  hitsData: PropTypes.object,
  id: PropTypes.string.isRequired
}

export async function getStaticProps(context) {
  prerenderImportantLocales(context.locale)

  const { id } = context.params
  const isUid = /^[a-zA-Z0-9]{15,}$/.test(id)
  const queryType = isUid ? 'bb' : 'slug'
  const language = context.locale.split('-')[0].toLowerCase()
  const country = context.locale.split('-')[1]?.toLowerCase() || 'us'

  try {
    const seriesResponse = await searchAPI_v2(
      `/content/metadata/${queryType}?id=${id}&type=series&append=credits%2Cseasons&language=${language}`
    )

    // Fetch hits data for the series
    let hitsData = null
    try {
      const seriesUid = seriesResponse.data.uid
      const hitsResponse = await searchAPI_v2(
        `/content/hits/${seriesUid}?country=${country}&type=series`
      )
      hitsData = hitsResponse.data
    } catch (error) {
      console.error(`Error fetching hits data:`, error)
      hitsData = null
    }

    // Fetch availability for all supported countries
    const availabilityData = {}
    const seriesUid = seriesResponse.data.uid

    // Fetch availability for all countries in parallel
    const availabilityPromises = hitsCountries.map(async (country) => {
      try {
        const availabilityUrl = `/content/availability/bb?id=${seriesUid}&type=series&country=${country.toLowerCase()}`
        const response = await searchAPI_v2(availabilityUrl)
        return { country: country.toLowerCase(), data: response.data }
      } catch (error) {
        console.error(`Error fetching availability for ${country}:`, error)
        return { country: country.toLowerCase(), data: { availability: [] } }
      }
    })

    const availabilityResults = await Promise.all(availabilityPromises)

    // Convert array of results to object keyed by country
    availabilityResults.forEach(({ country, data }) => {
      availabilityData[country] = data
    })

    return {
      props: {
        series: seriesResponse.data,
        availabilityData,
        hitsData,
        id: id,
        messages: (
          await import(`../../locales/${context.locale.split('-')[0]}/common.json`)
        ).default
      },
      revalidate: 60 * 60 * 24 * 10
    }
  } catch (error) {
    console.error('Error in getStaticProps:', error)
    return {
      notFound: true
    }
  }
}

export async function getStaticPaths() {
  try {
    const { data } = await searchAPI_v2('/content/hits?type=series', {
      params: {
        limit: 1
      }
    })

    const paths = data.map((id) => ({
      params: { id: id.toString() }
    }))

    return {
      paths,
      fallback: 'blocking'
    }
  } catch (error) {
    console.error(error)

    return {
      paths: [],
      fallback: 'blocking'
    }
  }
}
